/* eslint-disable @typescript-eslint/no-unused-vars */
// Strona listy produktów (wariant Wise - równoległy redesign, nie modyfikuje Products.tsx)

import React, { FC, useEffect, useState, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import qs from 'query-string';
import { Grid } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { ChevronLeft, Funnel } from 'react-bootstrap-icons';
import { Skeleton } from '@mui/material';

import { reduxActions, useDispatch, useSelector } from 'store';
import {
  useGetProducts,
  useGetProductsMutation,
  useGetProductsFiltersAdditional,
  useGetProductsFiltersMain,
  useGetProductsSortMethods,
  useGetProductsTitle,
  useGetCmsSectionArticle,
  useGetProductsBreadcrumbs
} from 'api';
import { IProductsRequest, IProductsSortMethod } from 'api/types';
import { useAppNavigate, useRWD, useScrollDown, usePrevious } from 'hooks';
import {
  WiseFiltersSidebar,
  WiseFiltersTopBar,
  WiseFiltersOverlay,
  WiseCategories
} from 'pages/wise/WiseProducts/components';
import {
  WiseProductsGridSkeleton,
  WiseFiltersSidebarSkeleton
} from 'pages/wise/WiseProducts/components/WiseSkeletons/WiseSkeletons';
import { Search, WiseBreadcrumbs, WiseProductItem, WiseMobileProductItem } from 'components/containers';
import { Container } from 'components/controls';
import WisePagination from 'components/controls/wise/WisePagination/WisePagination';
import WiseSelect from 'components/wise/WiseSelect';
import GridSwitcher, { IProps as IGridSwitcherProps } from 'components/controls/GridSwitcher';
import WiseGridSwitcher from 'components/controls/wise/WiseGridSwitcher/WiseGridSwitcher';
import FiltersSwitcher, { FiltersOrientation } from 'components/controls/FiltersSwitcher';
import { FiltersHorizontal } from 'pages/Products/components';
import { generateCategoryPageScheme } from 'api/helpers';

import styles from 'theme/pages/wise/WiseProducts/WiseProducts.module.scss';

interface IFilter {
  filter_id: string;
  filter_value: string;
}

interface IProps {
  categoryId?: string;
  mode?: 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS';
  h1?: string | null;
  hasSeoTitle?: boolean;
}

const WiseProducts: FC<IProps> = ({ categoryId, mode, h1, hasSeoTitle }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const location = useLocation();

  const { category_id } = qs.parse(location.search);

  const getPosition = (string: string, subString: string, index: number) => {
    return string.split(subString, index).join(subString).length;
  };

  const urlPrefix = location.pathname.slice(0, getPosition(location.pathname, '/', 2));

  const { isMobile, isTablet } = useRWD();
  const { isScrollDown } = useScrollDown();

  // kategoria pochodząca z url resolvera
  const urlResolverCategoryId = parseInt(categoryId || '0');

  const categoryIdQueryParam = urlResolverCategoryId
    ? { category_id: urlResolverCategoryId }
    : typeof category_id === 'string'
      ? { category_id: parseInt(category_id) }
      : {};

  // poprzednia wartość kategorii
  const prevCategoryId = usePrevious(categoryIdQueryParam.category_id);

  // globalnie ustawiona kategoria i fraza wyszukiwarki
  const { searchKeyword: globalSearchKeyword } = useSelector((state) => state.products);

  // poprzednia wartość kategorii
  const prevGlobalSearchKeyword = usePrevious(globalSearchKeyword);

  // Aktualne filtry
  const [queryFilters, setQueryFilters] = useState<IFilter[]>([]);
  const [isFilterMobile, setIsFilterMobile] = useState(false);

  const localStorageGridType = localStorage.getItem('gridType') as 'grid' | 'line' | null;

  // Widok listy (boxy/linie)
  const [gridType, setGridType] = useState<IGridSwitcherProps['type']>(
    localStorageGridType || 'grid'
  );

  // Orientacja filtrów (pionowe/poziome)
  const [filtersOrientation, setFiltersOrientation] = useState<FiltersOrientation>(
    (localStorage.getItem('filtersOrientation') as FiltersOrientation | null) || 'vertical'
  );

  //Używamy ref aby śledzić czy to pierwsze renderowanie
  const isFirstRender = useRef(true);

  const [productsQuery, setProductsQuery] = useState<IProductsRequest>({
    page: 1,
    limit: 20,
    search_keyword: '',
    filter_attributes: '',
    sort_method: '',
    mode: mode,
    ...categoryIdQueryParam,
    ...qs.parseUrl(window.location.href, { parseNumbers: false }).query
  });

  const { data: productsBreadcrumbsData, isFetching: isBredcrumbsLoading } =
    useGetProductsBreadcrumbs(productsQuery);

  const { data: mainFiltersData, isLoading: isMainFiltersLoading } = useGetProductsFiltersMain({
    ...productsQuery,
    page: 1,
    limit: 999
  });

  const { data: additionalFiltersData, isLoading: isAdditionalFiltersLoading } =
    useGetProductsFiltersAdditional({
      ...productsQuery,
      page: 1,
      limit: 999
    });

  const { data: productsSimpleData, isLoading: isLoadingProducts } = useGetProducts(
    {
      scopes:
        'BasicProductData|ImageFormattedProductData|UnitProductData|StockFormattedProductData|ShoppingListProductData',
      ...productsQuery
    },
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        refetchProductsFullData({
          scopes: 'PriceProductData',
          productIds: data.items
            .map((item) => item.id)
            .toString()
            .replaceAll(',', '|'),
          ...productsQuery
        });
      },
    }
  );

  // Pobranie listy produktów
  const { data: productsFullData, mutate: refetchProductsFullData, isLoading: isPriceLoading } = useGetProductsMutation();

  // Ceny są aktualne tylko jeśli dotyczą tych samych produktów co lista
  const arePricesSynced = !!(
    productsFullData &&
    productsSimpleData?.items?.[0]?.id &&
    productsFullData.items?.[0]?.id === productsSimpleData.items[0].id
  );

  const productsData = useMemo(
    () => ({
      ...productsSimpleData,
      ...productsFullData
    }),
    [productsSimpleData, productsFullData]
  );

  // Pobranie nagłówka
  const { data: productsTitleData } = useGetProductsTitle(productsQuery);

  // Pobranie opcji sortowania
  const { data: productsSortingMethodsData } = useGetProductsSortMethods({
    page: 1,
    limit: 999
  });

  const { data: seoDescriptionData, refetch: refetchSeoDescription } = useGetCmsSectionArticle(
    'CATEGORY_DESCRIPTION',
    categoryIdQueryParam.category_id?.toString() || '',
    {
      enabled: false
    }
  );

  const urlSearchKeyword = qs.parseUrl(location.search).query.search_keyword;

  useEffect(() => {
    typeof urlSearchKeyword === 'string' &&
    dispatch(reduxActions.setSearchKeyword(urlSearchKeyword));
  }, [urlSearchKeyword]);

  // ustawienie globalnej kategorii
  useEffect(() => {
    if (categoryIdQueryParam.category_id !== prevCategoryId && categoryIdQueryParam.category_id) {
      dispatch(reduxActions.setCategoryId(categoryIdQueryParam.category_id));
    }
  }, [categoryIdQueryParam]);

  // usuwanie globalnej kategorii
  useEffect(() => {
    return () => {
      dispatch(reduxActions.setCategoryId(undefined));
      dispatch(reduxActions.setSearchFilterCategory({ id: null, name: '', url_link: null }));
    };
  }, []);

  useEffect(() => {
    if (categoryIdQueryParam.category_id) {
      refetchSeoDescription();
    }
  }, [categoryIdQueryParam.category_id]);

  // Zmiana url'a przy zmianie parametrów zapytania do API
  useEffect(() => {
    const { limit, category_id, ...restQuery } = productsQuery;

    const navigationUrl = categoryId
      ? `${location.pathname.replace(urlPrefix, '')}?${qs.stringify(restQuery, {
        skipEmptyString: true
      })}`
      : `${location.pathname.replace(urlPrefix, '')}?${qs.stringify(
        { ...restQuery, category_id },
        {
          skipEmptyString: true
        }
      )}`;

    navigate(navigationUrl, { replace: true });
  }, [
    productsQuery.page,
    productsQuery.filter_attributes,
    productsQuery.sort_method,
    productsQuery.search_keyword
  ]);

  // Ustawienie aktywnych filtrów z url'a (podczas wejścia na stronę)
  useEffect(() => {
    if (isFirstRender.current) {
      typeof productsQuery.search_keyword === 'string' &&
      dispatch(reduxActions.setSearchKeyword(productsQuery.search_keyword));
      setQueryFilters(
        productsQuery.filter_attributes
          ?.split('|')
          .filter((item) => item)
          .map((queryFilter) => {
            const queryFilterArray = queryFilter.split('=');
            return {
              filter_id: queryFilterArray[0],
              filter_value: queryFilterArray[1]
            };
          }) || []
      );
      isFirstRender.current = false;
    }
  }, []);

  // Ustawienie breadcrumbs'ów (przy renderowaniu strony)
  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs(
        productsBreadcrumbsData
          ? productsBreadcrumbsData.items.map((item) => ({
            name: item.name,
            path: item.category_id ? `/${item.url}` : undefined
          }))
          : []
      )
    );
  }, [productsBreadcrumbsData?.items]);

  // aktualizacja parametrów zapytania przy zmianie kategorii
  useEffect(() => {
    // Pierwszy render: nie resetujemy filtrow - byly zainicjalizowane z URL (filter_attributes z linka).
    // Reset robimy tylko przy faktycznej zmianie kategorii (np. nawigacja z kategorii A do B).
    if (
      categoryIdQueryParam.category_id !== prevCategoryId &&
      prevCategoryId !== undefined
    ) {
      const currentUrlParams = qs.parseUrl(window.location.href, { parseNumbers: false }).query;
      const currentModeFromUrl = currentUrlParams.mode as 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS' | undefined;

      setProductsQuery((prevState) => ({
        ...prevState,
        mode: currentModeFromUrl || undefined,
        filter_attributes: '',
        page: 1,
        category_id: categoryIdQueryParam.category_id
      }));

      // Resetujemy również filtry w UI
      setQueryFilters([]);
    }
  }, [categoryIdQueryParam.category_id, prevCategoryId, categoryId, location.search]);

  useEffect(() => {
    if (globalSearchKeyword !== prevGlobalSearchKeyword && prevGlobalSearchKeyword !== undefined) {
      setProductsQuery((prevState) => ({
        ...prevState,
        mode: undefined,
        filter_attributes: '',
        search_keyword: globalSearchKeyword,
        page: 1
      }));

      // Resetujemy filtry
      setQueryFilters([]);
    }
  }, [globalSearchKeyword, prevGlobalSearchKeyword]);

  // Uaktualnienie filtrów w zapytaniu do API
  useEffect(() => {
    setProductsQuery((prevState) => ({
      ...prevState,
      filter_attributes: queryFilters
        .map((filter) => `${filter.filter_id}=${filter.filter_value}`)
        .join('|')
    }));
  }, [queryFilters]);

  // Uaktualnienie kategorii (w globalnym stanie) po zmianie tej danej w url'u
  useEffect(() => {
    const currentUrlParams = qs.parseUrl(window.location.href, { parseNumbers: false }).query;
    const currentModeFromUrl = currentUrlParams.mode as 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS' | undefined;
    const activeMode = mode || currentModeFromUrl;

    if (activeMode) {
      setProductsQuery((prevState) => ({
        ...prevState,
        limit: 30,
        mode: activeMode
      }));
    }
  }, [mode]);

  // Funkcja aktualizująa filtry (w stanie komponentu i zapytaniu do API)
  const updateQueryFilters = (filters: IFilter[]) => {
    setQueryFilters(filters);
    setProductsQuery((prevState) => ({
      ...prevState,
      filter_attributes: filters
        .map((filter) => `${filter.filter_id}=${filter.filter_value}`)
        .join('|'),
      page: 1
    }));
  };

  // Generowanie JSON-LD schema dla kategorii
  const categoryPageScheme = useMemo(() => {
    if (!productsSimpleData || !productsTitleData) return null;
    return generateCategoryPageScheme(
      productsSimpleData,
      productsTitleData.name,
      window.location.href
    );
  }, [productsSimpleData, productsTitleData]);

  const renderProducts = () =>
    productsSimpleData?.items?.map((simpleProduct) => {
      const product = productsFullData
        ? {
          ...simpleProduct,
          ...productsFullData?.items.find((fullProduct) => fullProduct.id === simpleProduct.id)
        }
        : simpleProduct;

      return (
        <Grid
          key={product.id}
          item
          md={gridType === 'line' ? 12 : isTablet ? 4 : 3}
          itemProp="itemListElement"
          itemScope
          itemType="http://schema.org/ListItem">
          {isMobile ? (
            <WiseMobileProductItem
              product={product}
              categoryId={categoryIdQueryParam.category_id}
              searchKeywords={globalSearchKeyword}
              isPriceLoading={isPriceLoading || !arePricesSynced}
            />
          ) : (
            <WiseProductItem
              product={product}
              line={gridType === 'line'}
              categoryId={categoryIdQueryParam.category_id}
              searchKeywords={globalSearchKeyword}
              isPriceLoading={isPriceLoading || !arePricesSynced}
            />
          )}
        </Grid>
      );
    });

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-wise-WiseProducts')}>
      {productsTitleData && (
        <Helmet>
          {!hasSeoTitle && <title>{productsTitleData.name}</title>}
          <link rel="canonical" href={window.location.href} />
          {categoryPageScheme && (
            categoryPageScheme.map((scheme, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(scheme)}
              </script>
            ))
          )}
        </Helmet>
      )}
      <WiseBreadcrumbs isLoading={isBredcrumbsLoading} />

      {isFilterMobile ? (
        isMobile && (
          <WiseFiltersOverlay
            categoryId={categoryIdQueryParam.category_id}
            onChange={updateQueryFilters}
            queryFilters={queryFilters}
            filtersData={[
              ...(mainFiltersData?.items || []),
              ...(additionalFiltersData?.items || [])
            ]}
            close={() => setIsFilterMobile(false)}
            productsSortingMethodsData={productsSortingMethodsData}
            productsQuery={productsQuery}
            setSort={(id: string) =>
              setProductsQuery({
                ...productsQuery,
                sort_method: id,
                page: 1
              })
            }
          />
        )
      ) : (
        <Container>
          <div className={styles.content}>
            {!isMobile && (
              <div className={styles.sidebar}>
                <div className={styles.categoriesCard}>
                  <span className={styles.Title}>{t('Kategorie')}</span>
                  <WiseCategories
                    searchKeyword={globalSearchKeyword}
                    chosenCategoryId={categoryIdQueryParam.category_id}
                    productsQueryParams={productsQuery}
                  />
                </div>

                {filtersOrientation === 'vertical' && (
                  <div className={styles.filtersCard}>
                    {isAdditionalFiltersLoading ? (
                      <WiseFiltersSidebarSkeleton />
                    ) : (
                      <WiseFiltersSidebar
                        categoryId={categoryIdQueryParam.category_id}
                        onChange={updateQueryFilters}
                        queryFilters={queryFilters}
                        filtersData={additionalFiltersData?.items}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            <div className={styles.list}>


              <div
                className={classnames(styles.bottomBar, { [styles.isScrollDown]: !isScrollDown })}>
                <div className={styles.title}>
                  {productsTitleData ? (
                    <>
                      <Link className={styles.arrow} to="/">
                        <ChevronLeft />
                      </Link>{' '}
                      <h1>{h1 || productsTitleData.name}</h1>{' '}
                      <span>({productsTitleData.products_count})</span>
                    </>
                  ) : (
                    <Skeleton width={200} height={50} />
                  )}
                </div>

                {seoDescriptionData?.article_fields?.[0]?.value && (
                  <h2
                    className={styles.seoBlock}
                    dangerouslySetInnerHTML={{
                      __html: seoDescriptionData?.article_fields?.[0].value
                    }}
                  />
                )}

                <div className={styles.filterWrapper}>
                  <div className={styles.filterInfo}>
                    <span>
                      <Trans>Wyniki wyszukiwania</Trans>
                    </span>
                    <span>({productsSimpleData?.total_count})</span>
                  </div>

                  <div onClick={() => setIsFilterMobile(true)} className={styles.filterButton}>

                    <div>
                      <Trans>Filtruj</Trans>/<Trans>Sortuj</Trans>
                    </div>
                  </div>
                </div>
              </div>

              {!isMobile && (
                <Grid item sm={12}>
                  <div className={styles.actionsTopBar}>
                    {isMainFiltersLoading ? (
                      <Skeleton height={34} width={300} />
                    ) : (
                      <WiseFiltersTopBar
                        onChange={updateQueryFilters}
                        queryFilters={queryFilters}
                        filtersData={mainFiltersData?.items}
                      />
                    )}

                    <div>
                      <div className={styles.sortingSelectWrapper}>
                        <WiseSelect<IProductsSortMethod>
                          onChange={(sortMethod) =>
                            sortMethod &&
                            setProductsQuery({
                              ...productsQuery,
                              sort_method: sortMethod.id,
                              page: 1
                            })
                          }
                          value={productsQuery.sort_method}
                          options={
                            productsSortingMethodsData?.items.map((item) => ({
                              value: item.id,
                              label: item.name,
                              item
                            })) || []
                          }
                          placeholder={t('Sortowanie')}
                          styleGroup="wise-filters"
                        />
                      </div>

                      <div style={{ marginRight: 16 }}>
                        <FiltersSwitcher
                          orientation={filtersOrientation}
                          onChange={(orientation) => {
                            localStorage.setItem('filtersOrientation', orientation);
                            setFiltersOrientation(orientation);
                          }}
                        />
                      </div>
                      <div className={styles.gridSwitcherWrapper}>
                        <WiseGridSwitcher
                          type={gridType}
                          onChange={(type) => {
                            localStorage.setItem('gridType', type);
                            setGridType(type);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Grid>
              )}

              {!isMobile && (filtersOrientation === 'horizontal-wrap' || filtersOrientation === 'horizontal-row') && (
                <FiltersHorizontal
                  categoryId={categoryIdQueryParam.category_id}
                  onChange={updateQueryFilters}
                  queryFilters={queryFilters}
                  filtersData={[
                    ...(mainFiltersData?.items || []),
                    ...(additionalFiltersData?.items || [])
                  ].filter((f) => f.type !== 'range')}
                  wrap={filtersOrientation === 'horizontal-wrap'}
                />
              )}

              <Grid
                container
                columnSpacing="12px"
                rowSpacing="12px"
                sx={isMobile ? { px: '12px' } : undefined}
                itemScope
                itemType="http://schema.org/ItemList">
                {isLoadingProducts && !productsSimpleData ? (
                  <WiseProductsGridSkeleton
                    count={isMobile ? 4 : gridType === 'line' ? 5 : 8}
                    line={gridType === 'line'}
                    isTablet={isTablet}
                    isMobile={isMobile}
                  />
                ) : (
                  renderProducts()
                )}
              </Grid>
            </div>
          </div>
          <WisePagination
            pagesCount={productsSimpleData?.total_pages || 1}
            page={productsQuery.page || 1}
            onChange={(page) => setProductsQuery({ ...productsQuery, page })}
          />
          {seoDescriptionData?.article_fields?.[1]?.value && (
            <h2
              className={styles.seoBlock}
              dangerouslySetInnerHTML={{ __html: seoDescriptionData?.article_fields?.[1].value }}
            />
          )}
        </Container>
      )}
    </div>
  );
};

export default WiseProducts;
